import type {
  ImportedPrepDeck,
  InventoryEntry,
  SavedDeck,
  SavedPrepPlan,
  StoredSideboardPrep,
} from "@/lib/local-store";
import type { SideGuide } from "@/lib/side-guides";

const DECKS_KEY = "rifthub.decks.v1";
const INVENTORY_KEY = "rifthub.inventory.v1";
const PREPS_KEY = "rifthub.sideboard.v1";
const PREPA_KEY = "rifthub.prepa.v1";
const IMPORTED_PREPA_DECKS_KEY = "rifthub.prepa.imported-decks.v1";
const SIDE_GUIDES_KEY = "rifthub.side-guides.v1";

export type AuthUser = {
  id: number;
  email: string;
  displayName: string | null;
};

export type CloudBundle = {
  inventory: InventoryEntry[];
  decks: SavedDeck[];
  preps: StoredSideboardPrep[];
  prepPlans: SavedPrepPlan[];
  importedPrepDecks: ImportedPrepDeck[];
  sideGuides: SideGuide[];
};

function writeLocal(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

function readLocal<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function readLocalBundle(): CloudBundle {
  return {
    inventory: readLocal<InventoryEntry[]>(INVENTORY_KEY, []),
    decks: readLocal<SavedDeck[]>(DECKS_KEY, []).map((d) => ({
      ...d,
      sideboard: d.sideboard ?? [],
      battlefieldIds: d.battlefieldIds ?? [],
    })),
    preps: readLocal<StoredSideboardPrep[]>(PREPS_KEY, []),
    prepPlans: readLocal<SavedPrepPlan[]>(PREPA_KEY, []),
    importedPrepDecks: readLocal<ImportedPrepDeck[]>(IMPORTED_PREPA_DECKS_KEY, []),
    sideGuides: readLocal<SideGuide[]>(SIDE_GUIDES_KEY, []),
  };
}

export function applyCloudBundle(data: CloudBundle) {
  writeLocal(INVENTORY_KEY, data.inventory ?? []);
  writeLocal(DECKS_KEY, data.decks ?? []);
  writeLocal(PREPS_KEY, data.preps ?? []);
  writeLocal(PREPA_KEY, data.prepPlans ?? []);
  writeLocal(IMPORTED_PREPA_DECKS_KEY, data.importedPrepDecks ?? []);
  writeLocal(SIDE_GUIDES_KEY, data.sideGuides ?? []);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("rifthub:storage-sync"));
  }
}

let cloudEnabled = false;

export function isCloudEnabled() {
  return cloudEnabled;
}

export function setCloudEnabled(enabled: boolean) {
  cloudEnabled = enabled;
}

async function patch(body: unknown) {
  if (!cloudEnabled) return;
  try {
    await fetch("/api/me/data", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
    });
  } catch {
    // Keep local write; sync can retry later via full push
  }
}

export async function syncInventoryQuantity(cardId: string, quantity: number) {
  await patch({ kind: "inventory", cardId, quantity });
}

export async function syncInventoryReplace(inventory: InventoryEntry[]) {
  await patch({ kind: "inventory-replace", inventory });
}

export async function syncDeck(deck: SavedDeck) {
  await patch({ kind: "deck", deck });
}

export async function syncDeckDelete(id: string) {
  await patch({ kind: "deck-delete", id });
}

export async function syncPrep(prep: StoredSideboardPrep) {
  await patch({ kind: "prep", prep });
}

export async function syncPrepDelete(id: string) {
  await patch({ kind: "prep-delete", id });
}

export async function syncPrepPlan(plan: SavedPrepPlan) {
  await patch({ kind: "prep-plan", plan });
}

export async function syncPrepPlanDelete(id: string) {
  await patch({ kind: "prep-plan-delete", id });
}

export async function syncImportedPrepDeck(deck: ImportedPrepDeck) {
  await patch({ kind: "imported-prep-deck", deck });
}

export async function syncImportedPrepDeckDelete(id: string) {
  await patch({ kind: "imported-prep-deck-delete", id });
}

export async function syncSideGuide(guide: SideGuide) {
  await patch({ kind: "side-guide", guide });
}

export async function syncSideGuideDelete(id: string) {
  await patch({ kind: "side-guide-delete", id });
}

export async function fetchMe(): Promise<AuthUser | null> {
  const res = await fetch("/api/auth/me", { credentials: "same-origin" });
  if (!res.ok) return null;
  const data = (await res.json()) as { user: AuthUser | null };
  return data.user ?? null;
}

export async function pullCloud(): Promise<CloudBundle> {
  const res = await fetch("/api/me/data", { credentials: "same-origin" });
  if (!res.ok) throw new Error("Impossible de charger les données cloud");
  const data = (await res.json()) as Partial<CloudBundle>;
  return {
    inventory: data.inventory ?? [],
    decks: data.decks ?? [],
    preps: data.preps ?? [],
    prepPlans: data.prepPlans ?? [],
    importedPrepDecks: data.importedPrepDecks ?? [],
    sideGuides: data.sideGuides ?? [],
  };
}

export async function pushCloud(bundle: CloudBundle = readLocalBundle()): Promise<void> {
  const res = await fetch("/api/me/data", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bundle),
    credentials: "same-origin",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || "Impossible d'envoyer les données cloud");
  }
}

function bundleHasData(b: CloudBundle) {
  return (
    b.inventory.length > 0 ||
    b.decks.length > 0 ||
    b.preps.length > 0 ||
    b.prepPlans.length > 0 ||
    b.importedPrepDecks.length > 0 ||
    (b.sideGuides?.length ?? 0) > 0
  );
}

/** After login: if cloud empty and local has data → push; else pull cloud into local. */
export async function reconcileAfterAuth(): Promise<"pulled" | "pushed" | "empty"> {
  const remote = await pullCloud();
  const local = readLocalBundle();

  if (!bundleHasData(remote) && bundleHasData(local)) {
    await pushCloud(local);
    return "pushed";
  }
  if (bundleHasData(remote)) {
    applyCloudBundle(remote);
    return "pulled";
  }
  return "empty";
}
